import { useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  getCompletionStepIds,
  FLAT_STEP_CONFIGS,
} from '@/features/guided-financial-planning/config';
import {
  guidedPlanningApi,
  guidedPlanningSessionKeys,
} from '@/features/guided-financial-planning/api';

const DEFAULT_COMPLETION_STEP_IDS = getCompletionStepIds(FLAT_STEP_CONFIGS);

function selectActiveSession(
  sessions: GuidedPlanningSessionResponse[] | undefined,
): GuidedPlanningSessionResponse | undefined {
  if (!sessions || sessions.length === 0) return undefined;

  const inProgressSession = sessions.find(s => s.status === 'in_progress');
  if (inProgressSession) return inProgressSession;

  const completedSession = sessions.find(s => s.status === 'completed');
  if (completedSession) return completedSession;

  return sessions[0];
}

export function useGuidedPlanningSession(
  clientId: string,
  completionStepIds: GuidedPlanningStep[] = DEFAULT_COMPLETION_STEP_IDS,
) {
  const queryClient = useQueryClient();

  const {
    data: sessions,
    isLoading,
    isError,
    error,
    refetch,
  } = useQuery({
    queryKey: guidedPlanningSessionKeys.list({ clientId }),
    queryFn: () => guidedPlanningApi.getSessions(clientId),
    enabled: Boolean(clientId),
    staleTime: 2 * 60 * 1000,
  });

  const session = useMemo(() => selectActiveSession(sessions), [sessions]);

  const upsertMutation = useMutation({
    mutationFn: (data: PutGuidedPlanningSessionBody) =>
      guidedPlanningApi.upsertSession(clientId, data),
    onSuccess: updatedSession => {
      queryClient.setQueryData(
        guidedPlanningSessionKeys.list({ clientId }),
        (oldSessions: GuidedPlanningSessionResponse[] | undefined) => {
          if (!oldSessions) return [updatedSession];
          const index = oldSessions.findIndex(s => s.id === updatedSession.id);
          if (index >= 0) {
            const newSessions = [...oldSessions];
            newSessions[index] = updatedSession;
            return newSessions;
          }
          return [updatedSession, ...oldSessions];
        },
      );
    },
  });

  const updateStep = (step: GuidedPlanningStep, markPreviousComplete = true) => {
    if (!session?.id || session.status === 'completed') {
      return Promise.resolve(undefined);
    }

    const completedSteps = markPreviousComplete
      ? [...new Set([...(session.completedSteps ?? []), session.currentStep])]
      : (session.completedSteps ?? []);

    return upsertMutation.mutateAsync({
      sessionId: session.id,
      currentStep: step,
      completedSteps: completedSteps as GuidedPlanningStep[],
    });
  };

  const updateStepData = (newData: GuidedPlanningStepData) => {
    if (!session?.id || session.status === 'completed') {
      return Promise.resolve(undefined);
    }

    return upsertMutation.mutateAsync({
      sessionId: session.id,
      stepData: { ...session.stepData, ...newData },
    });
  };

  const completeStepWithData = (
    nextStep: GuidedPlanningStep,
    stepData?: GuidedPlanningStepData,
  ) => {
    if (!session?.id || session.status === 'completed') {
      return Promise.resolve(undefined);
    }

    const completedSteps = [
      ...new Set([...(session.completedSteps ?? []), session.currentStep]),
    ] as GuidedPlanningStep[];

    return upsertMutation.mutateAsync({
      sessionId: session.id,
      currentStep: nextStep,
      completedSteps,
      stepData: stepData ? { ...session.stepData, ...stepData } : undefined,
    });
  };

  const completeSession = () => {
    if (!session?.id || session.status === 'completed') {
      return Promise.resolve(undefined);
    }

    return upsertMutation.mutateAsync({
      sessionId: session.id,
      status: 'completed',
      completedSteps: completionStepIds,
    });
  };

  const abandonSession = () => {
    if (!session?.id) return Promise.resolve(undefined);

    return upsertMutation.mutateAsync({
      sessionId: session.id,
      status: 'abandoned',
    });
  };

  return {
    session,
    sessions,
    isLoading,
    isError,
    error,
    isSaving: upsertMutation.isPending,
    updateStep,
    updateStepData,
    completeStepWithData,
    completeSession,
    abandonSession,
    refetch,
  };
}
